import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PlayCircle, ArrowRight } from "lucide-react";
import { TcrLayout } from "@/tcr/TcrLayout";
import { TwoWayModel } from "@/tcr/TwoWayModel";
import { ActPackage } from "@/tcr/ActPackage";
import { ActImpact } from "@/tcr/ActImpact";
import { ActRollout } from "@/tcr/ActRollout";
import { ActInstall } from "@/tcr/ActInstall";
import { ActService } from "@/tcr/ActService";
import { RoleViewPanel } from "@/tcr/RoleViewPanel";
import { WALK_STEPS, WalkthroughOverlay, applyWalkHighlights, clearWalkHighlights } from "@/tcr/Walkthrough";
import { SCENARIOS, VIEWS, VIEW_NOTES, CLOSING_CHAIN, DISCLAIMERS, FINAL_MESSAGE } from "@/tcr/data";
import { StatusChip } from "@/tcr/ui";

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "package", label: "Change Package" },
    { key: "impact", label: "Local Impact" },
    { key: "rollout", label: "Rollout" },
    { key: "install", label: "Installation" },
    { key: "service", label: "Service Release" },
];

const SCENARIO_TAB = {
    blocked: "install",
    success: "install",
    denied: "impact",
    excluded: "impact",
    rollback: "install",
};

export default function TrustedRelease() {
    const [searchParams, setSearchParams] = useSearchParams();
    const rawTab = searchParams.get("tab");
    const tab = TABS.some((t) => t.key === rawTab) ? rawTab : "overview";
    const setTab = (k) => setSearchParams({ tab: k });

    const [scenario, setScenario] = useState("blocked");
    const [view, setView] = useState("network");
    const [stage, setStage] = useState("start");
    const [walk, setWalk] = useState({ active: false, step: 0, paused: false, completed: false });
    const walkTimer = useRef(null);

    const pickScenario = (key) => {
        setScenario(key);
        setStage("start");
        setTab(SCENARIO_TAB[key]);
    };

    const startWalk = () => setWalk({ active: true, step: 0, paused: false, completed: false });
    const exitWalk = () => {
        clearWalkHighlights();
        setWalk({ active: false, step: 0, paused: false, completed: false });
    };
    const stepWalk = (dir) =>
        setWalk((w) => {
            const next = w.step + dir;
            if (next >= WALK_STEPS.length) return { ...w, completed: true };
            if (next < 0) return w;
            return { ...w, step: next };
        });

    useEffect(() => {
        if (!walk.active || walk.completed) {
            if (walk.completed) clearWalkHighlights();
            return;
        }
        const s = WALK_STEPS[walk.step];
        setTab(s.tab);
        if (s.scenario) setScenario(s.scenario);
        if (s.stage) setStage(s.stage);
        const t = setTimeout(() => applyWalkHighlights(s.targets), 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [walk.active, walk.step, walk.completed]);

    useEffect(() => {
        if (!walk.active || walk.paused || walk.completed) return;
        walkTimer.current = setTimeout(() => stepWalk(1), 7000);
        return () => clearTimeout(walkTimer.current);
    }, [walk.active, walk.step, walk.paused, walk.completed]);

    return (
        <TcrLayout>
            <section className="fade-up" data-testid="release-hero">
                <div className="flex flex-wrap items-center gap-3">
                    <p className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400">
                        Controlled software and configuration release · healthcare imaging workstation
                    </p>
                    <StatusChip tone="amber" testId="release-mode-badge">SIMULATED</StatusChip>
                </div>
                <h1 className="mt-4 max-w-4xl text-3xl font-medium leading-tight tracking-tight text-slate-900 sm:text-4xl">
                    The release journey — publish, assess, plan, install, release, observe
                </h1>
                <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
                    A manufacturer publishes change package TCR-2027-021 (all dates and
                    identifiers are synthetic). No single organisation holds all the
                    evidence or all the authority required to declare the change ready
                    for clinical service — so the journey is governed across five
                    separately operated role nodes.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button
                        onClick={startWalk}
                        data-testid="walkthrough-start-btn"
                        className="inline-flex items-center gap-2 rounded-sm bg-blue-950 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                    >
                        <PlayCircle className="h-4 w-4" strokeWidth={1.5} />
                        Run guided release
                    </button>
                    <button
                        onClick={() => setTab("package")}
                        data-testid="release-cta-start"
                        className="group inline-flex items-center gap-2 rounded-sm border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-900 transition-colors duration-150 hover:bg-slate-50"
                    >
                        Start at the change package
                        <ArrowRight className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" />
                    </button>
                </div>
            </section>

            <div
                className="sticky top-[57px] z-20 -mx-6 mt-8 border-y border-slate-200 bg-white/95 px-6 py-2.5 backdrop-blur"
                data-testid="release-controls"
            >
                <div className="flex flex-wrap items-center gap-1">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`release-tab-${t.key}`}
                            className={`rounded-sm px-3 py-1.5 text-sm transition-colors duration-150 ${
                                tab === t.key
                                    ? "bg-blue-950 font-medium text-white"
                                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 pb-0.5 text-xs">
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="scenario-controls">
                        <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400">Scenario</span>
                        {SCENARIOS.map((s) => (
                            <button
                                key={s.key}
                                onClick={() => pickScenario(s.key)}
                                data-testid={`scenario-${s.key}`}
                                className={`rounded-sm border px-2 py-1 transition-colors duration-150 ${
                                    scenario === s.key
                                        ? "border-blue-950 bg-blue-50 text-blue-950"
                                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                                }`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="view-controls">
                        <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400">View</span>
                        {VIEWS.map((v) => (
                            <button
                                key={v.key}
                                onClick={() => setView(v.key)}
                                data-testid={`view-${v.key}`}
                                className={`rounded-sm border px-2 py-1 transition-colors duration-150 ${
                                    view === v.key
                                        ? "border-blue-950 bg-blue-50 text-blue-950"
                                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                                }`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <p className="mt-4 text-xs leading-relaxed text-slate-500" data-testid="view-note">
                {VIEW_NOTES[view]}
            </p>

            {view !== "network" && (
                <div className="mt-4">
                    <RoleViewPanel view={view} />
                </div>
            )}

            <div className="mt-8 space-y-16">
                {tab === "overview" && (
                    <>
                        <TwoWayModel />
                        <FinalScreen />
                    </>
                )}
                {tab === "package" && <ActPackage />}
                {tab === "impact" && <ActImpact view={view} scenario={scenario} />}
                {tab === "rollout" && <ActRollout />}
                {tab === "install" && <ActInstall scenario={scenario} stage={stage} setStage={setStage} />}
                {tab === "service" && <ActService />}
            </div>

            <section className="mt-16 rounded-sm border border-slate-200 bg-white p-6" data-testid="release-disclaimers">
                <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">Disclaimers</p>
                <ul className="mt-3 grid gap-x-8 gap-y-1.5 text-xs leading-relaxed text-slate-500 sm:grid-cols-2">
                    {DISCLAIMERS.map((d) => <li key={d}>· {d}</li>)}
                </ul>
            </section>

            {walk.active && (
                <WalkthroughOverlay
                    step={walk.step}
                    paused={walk.paused}
                    completed={walk.completed}
                    onPrev={() => stepWalk(-1)}
                    onNext={() => stepWalk(1)}
                    onPauseToggle={() => setWalk((w) => ({ ...w, paused: !w.paused }))}
                    onExit={exitWalk}
                    onReplay={startWalk}
                />
            )}
        </TcrLayout>
    );
}

function FinalScreen() {
    return (
        <section className="rounded-sm border border-slate-200 bg-white p-8" data-testid="release-final-screen">
            <h2 className="text-2xl font-medium tracking-tight text-slate-900">
                One change. Separate authorities. A governed release journey.
            </h2>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">{FINAL_MESSAGE}</p>
            <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="release-closing-chain">
                {CLOSING_CHAIN.map((s, i) => (
                    <span key={s} className="flex items-center gap-2">
                        <span className="rounded-sm bg-blue-950 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wide text-white">
                            {s}
                        </span>
                        {i < CLOSING_CHAIN.length - 1 && <span className="text-slate-300">→</span>}
                    </span>
                ))}
            </div>
        </section>
    );
}
